import React, { useEffect, useMemo, useState } from 'react';
import 'src/components/announcement/AnnouncementBannerList.scss';
import './AdminBannerPage.scss';

const defaultForm = {
  message: '',
  linkUrl: '',
  severity: 'info',
  isActive: true,
  // startsAt: '',
  // endsAt: '',
};

const severities = [
  { id: 'info', label: 'Info' },
  { id: 'warning', label: 'Warning' },
  { id: 'critical', label: 'Critical' },
];

export default function AdminBannerPage() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const resp = await fetch('/popin/admin/banners', { credentials: 'include' });
      const json = await resp.json();
      if (json && json.banners) {
        setBanners(json.banners);
      } else {
        setBanners([]);
      }
      setError(json && json.error ? json.error : null);
    } catch (e) {
      console.log('AdminBannerPage load error', e);
      setError('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (b) => {
    setForm({
      message: b.message || '',
      linkUrl: b.linkUrl || '',
      severity: b.severity || 'info',
      isActive: !!b.isActive,
    });
    setEditId(b.id);
    setSaved(false);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(defaultForm);
  };

  useEffect(() => { load(); }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? !!checked : value }));
    setSaved(false);
  };

  const save = async (evt) => {
    evt.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body = new URLSearchParams();
      body.set('message', form.message);
      if (form.linkUrl) body.set('linkUrl', form.linkUrl);
      if (form.severity) body.set('severity', form.severity);
      body.set('isActive', form.isActive ? 'true' : 'false');
      // Optional schedule
      // if (form.startsAt) body.set('startsAt', form.startsAt);
      // if (form.endsAt) body.set('endsAt', form.endsAt);

      let url = '/popin/admin/banners';
      let method = 'POST';
      if (editId != null) {
        // Update existing banner via POST to dedicated endpoint for compatibility
        url = '/popin/admin/banners/update';
        method = 'POST';
        body.set('id', String(editId));
      }

      const resp = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const json = await resp.json();
      if (json && json.error) {
        setError(json.error);
      } else {
        setSaved(true);
      }
      await load();
      setForm(defaultForm);
      setEditId(null);
    } catch (e) {
      console.log('AdminBannerPage save error', e);
      setError('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      const body = new URLSearchParams();
      body.set('id', String(id));
      body.set('isActive', isActive ? 'true' : 'false');
      const resp = await fetch('/popin/admin/banners/toggle', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const json = await resp.json();
      if (json && json.error) {
        setError(json.error);
      }
      await load();
    } catch (e) {
      console.log('AdminBannerPage toggle error', e);
      setError('Failed to toggle banner');
    }
  };

  const remove = async (id) => {
    try {
      const body = new URLSearchParams();
      body.set('id', String(id));
      const resp = await fetch('/popin/admin/banners/delete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const json = await resp.json();
      if (json && json.error) {
        setError(json.error);
      }
      await load();
    } catch (e) {
      console.log('AdminBannerPage delete error', e);
      setError('Failed to delete banner');
    }
  };

  const sorted = useMemo(() => {
    return [...(banners || [])].sort((a, b) => {
      const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bt - at;
    });
  }, [banners]);

  const isMessageValid = (form.message || '').trim().length > 0;
  const isLinkValid = !form.linkUrl || /^https?:\/\/.+/.test(form.linkUrl);
  const canSubmit = isMessageValid && isLinkValid && !saving;

  const Preview = () => {
    const cls = `announcement-banner announcement-${form.severity || 'info'}`;
    return (
      <div className="announcement-preview">
        <div className="announcement-preview-title">Preview</div>
        <div className={cls} role="status" aria-live="polite">
          <div className="announcement-content">
            <div className="announcement-message">{form.message || 'Your banner message will appear here'}</div>
            {form.linkUrl ? (
              <a className="announcement-link" href={form.linkUrl} onClick={(e)=>e.preventDefault()}>Learn more</a>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const SeverityPills = () => (
    <div className="severity-pills" role="group" aria-label="Severity">
      {severities.map(s => (
        <button
          key={s.id}
          type="button"
          onClick={() => setForm(f => ({ ...f, severity: s.id }))}
          className={`pill pill-${s.id} ${form.severity === s.id ? 'active' : ''}`}
          aria-pressed={form.severity === s.id}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
    } catch (e) {
      console.log('Copy failed', e);
    }
  };

  return (
    <div className="container admin-banner-page" style={{ padding: 16 }}>
      <div className="page-header">
        <h2>Announcement Banners</h2>
        <div className="page-subtitle">Create announcements shown on the user dashboard. Multiple active banners will stack for users.</div>
      </div>
      {error && (
        <div className="alert alert-warning" style={{ marginTop: 10 }}>{String(error)}</div>
      )}
      {saved && (
        <div className="alert alert-success" style={{ marginTop: 10 }}>Banner created.</div>
      )}

      <Preview />

      <form onSubmit={save} className="card admin-banner-form" style={{ padding: 16, marginTop: 16 }}>
        <div className="form-group">
          <label className="form-label-strong">Message <span className="req">*</span></label>
          <textarea
            className="form-control"
            required
            name="message"
            value={form.message}
            onChange={onChange}
            rows={3}
            placeholder="e.g., New Feature: AI Question Generator"
          />
          {!isMessageValid && (
            <div className="field-hint error">Message is required</div>
          )}
        </div>

        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label-strong">Link URL (optional)</label>
          <input
            className="form-control"
            name="linkUrl"
            value={form.linkUrl}
            onChange={onChange}
            placeholder="https://support.your-intercom-article"
            pattern="https?://.+"
            title="Must start with http:// or https://"
          />
          {form.linkUrl && !isLinkValid && (
            <div className="field-hint error">Must start with http:// or https://</div>
          )}
        </div>

        <div className="banner-controls">
          <div className="controls-left">
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label-strong">Severity</label>
              <SeverityPills />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="active-toggle">
                <input id="abActive" className="form-check-input" type="checkbox" name="isActive" checked={!!form.isActive} onChange={onChange} />
                <span>Active</span>
              </label>
            </div>
          </div>
          <div className="controls-right">
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {saving ? 'Saving...' : (editId != null ? 'Update Banner' : 'Create Banner')}
            </button>
            {editId != null && (
              <button type="button" className="btn btn-link" onClick={cancelEdit}>Cancel</button>
            )}
          </div>
        </div>
      </form>

      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <h4>Existing Banners</h4>
        {loading ? (
          <div style={{ padding: 8 }}>Loading...</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Message</th>
                  <th>Severity</th>
                  <th>Active</th>
                  <th>Link</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => (
                  <tr key={b.id}>
                    <td>{b.id}</td>
                    <td className="msg-cell">
                      <div className="msg-wrap" title={b.message}>
                        {b.message}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-pill badge-${b.severity || 'info'}`}>{b.severity}</span>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!b.isActive}
                        onChange={(e) => toggleActive(b.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      {b.linkUrl ? (
                        <div className="link-cell">
                          <a href={b.linkUrl} target="_blank" rel="noopener noreferrer">Open</a>
                          <button type="button" className="btn btn-sm btn-link" onClick={() => copyToClipboard(b.linkUrl)} aria-label="Copy link">Copy</button>
                        </div>
                      ) : (<span style={{ color: '#888' }}>—</span>)}
                    </td>
                    <td>{new Date(b.updatedAt || b.createdAt).toLocaleString()}</td>
                    <td className="actions-cell">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(b)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(b.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ color: '#888' }}>No banners</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
